import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ChartLineUp, TrendUp, Info } from '@phosphor-icons/react';
import { investmentsAPI, formatApiError } from '../../services/api';
import { toast } from 'sonner';
import { HowInvestingWorksModal } from './HowInvestingWorksModal';

export function InvestmentSection({ product, productId, isAuthenticated, isOwnProduct, formatCurrency, navigate }) {
  const [investors, setInvestors] = useState([]);
  const [investorInfo, setInvestorInfo] = useState(null);
  const [investing, setInvesting] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  const loadInvestors = async () => {
    try {
      const { data } = await investmentsAPI.getProductInvestors(productId);
      setInvestors(data.investors || []);
      setInvestorInfo(data);
    } catch (err) { console.error('Failed to load investors:', err); }
  };

  useEffect(() => {
    loadInvestors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const handleInvest = async () => {
    if (!isAuthenticated) { toast.error('Please login to invest'); navigate('/login'); return; }
    if (!window.confirm(`Invest ${formatCurrency(product.investment_amount)} for ${product.investment_percentage}% revenue share on each sale?`)) return;
    setInvesting(true);
    try {
      await investmentsAPI.invest(productId);
      toast.success('Investment successful!');
      loadInvestors();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setInvesting(false); }
  };

  const canInvest = !isOwnProduct && investorInfo?.remaining_percentage >= product.investment_percentage;
  const slotsFull = investorInfo?.remaining_percentage < product.investment_percentage && !isOwnProduct;

  return (
    <Card className="mt-6 bg-[#0D1F17] border border-[#22C55E]/20 rounded-sm shadow-none" data-testid="investment-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <ChartLineUp className="w-5 h-5 text-[#22C55E] shrink-0" />
            <span className="text-sm font-bold text-white truncate">DiviSpend — Invest in This Product</span>
          </div>
          <button
            onClick={() => setShowHowItWorks(true)}
            className="flex items-center gap-1 text-[11px] text-[#22C55E] hover:text-[#4ADE80] transition-colors shrink-0 whitespace-nowrap"
            data-testid="how-investing-works-trigger"
          >
            <Info className="w-3.5 h-3.5" />
            How it works
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-[#111111] rounded-sm p-3 text-center">
            <p className="text-lg font-bold text-white">{formatCurrency(product.investment_amount)}</p>
            <p className="text-[10px] text-[#999999] uppercase tracking-wider">Investment</p>
          </div>
          <div className="bg-[#111111] rounded-sm p-3 text-center">
            <p className="text-lg font-bold text-[#22C55E]">{product.investment_percentage}%</p>
            <p className="text-[10px] text-[#999999] uppercase tracking-wider">Per Sale</p>
          </div>
        </div>
        {investorInfo && (
          <div className="flex items-center justify-between text-xs text-[#999999] mb-4">
            <span>{investorInfo.remaining_percentage}% remaining</span>
            <span>{investors.length} investor{investors.length !== 1 ? 's' : ''}</span>
          </div>
        )}
        {canInvest && (
          <Button
            onClick={handleInvest}
            disabled={investing}
            className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-11 font-semibold"
            data-testid="invest-button"
          >
            <TrendUp className="w-4 h-4 mr-2" />
            {investing ? 'Processing...' : `Invest ${formatCurrency(product.investment_amount)} for ${product.investment_percentage}%`}
          </Button>
        )}
        {slotsFull && (
          <p className="text-center text-sm text-[#666666]">Investment slots full</p>
        )}
        <p className="text-[10px] text-[#666666] mt-3 text-center">Earn {product.investment_percentage}% of each sale until you recoup your {formatCurrency(product.investment_amount)} investment.</p>

        {investors.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#22C55E]/10">
            <p className="text-[10px] uppercase tracking-wider text-[#999999] font-semibold mb-2">Investors</p>
            <div className="space-y-2">
              {investors.map((inv) => (
                <div key={inv.investment_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {inv.investor?.avatar_url ? (
                      <img src={inv.investor.avatar_url} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-[#22C55E]/10 flex items-center justify-center text-[#22C55E] text-[10px] font-bold">{inv.investor?.name?.[0] || '?'}</div>
                    )}
                    <span className="text-xs text-white">{inv.investor?.name || 'Investor'}</span>
                  </div>
                  <span className="text-xs text-[#22C55E] font-semibold">{inv.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
      <HowInvestingWorksModal
        open={showHowItWorks}
        onOpenChange={setShowHowItWorks}
        product={product}
        formatCurrency={formatCurrency}
      />
    </Card>
  );
}
